import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, DestroyRef, inject, signal, Signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { form, FormField, FormRoot, SchemaPathTree, submit } from '@angular/forms/signals';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TimerService } from '@app/components/active-timer-bar/timer.service';
import {
  TaskTimerComponent,
  TaskTimerMode
} from '@app/components/task/task-state-selectors/task-timer/task-timer.component';
import { TimeReportDialogService } from '@app/components/time-reports/time-report-dialog/time-report-dialog.service';
import {
  ReportSeriesDailyRange,
  ReportSeriesForm
} from '@app/shared/components/report-entry/report-series/report-series-form';
import { ReportSeriesSectionComponent } from '@app/shared/components/report-entry/report-series/report-series-section.component';
import { TimeReportsActionsService } from '@app/shared/services/actions/time-reports-actions.service';
import { AuthService } from '@app/shared/services/auth.service';
import { ProjectsDataService } from '@app/shared/services/projects-data.service';
import { ToastService } from '@app/shared/services/shared/toast.service';
import { TimeReportsDataService } from '@app/shared/services/time-reports-data.service';
import { Project } from '@app/shared/types/models/project/project.model';
import { WorkTimeReport } from '@app/shared/types/models/reports/work-time-report';
import { TimePickerValue } from '@app/shared/types/models/shared/time-picker-value';
import { TimeReportingType } from '@app/shared/types/models/shared/time-reporting-type';
import { TaskTimer } from '@app/shared/types/models/task/task-timer';
import { Task } from '@app/shared/types/models/task/task.model';
import { ReportUserSelector } from '@app/shared/types/report-user-selector';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { UiChoiceGroupComponent } from '@app/shared/ui/choice-group/ui-choice-group.component';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { UiTextareaComponent } from '@app/shared/ui/textarea/ui-textarea.component';
import { ProffeoUiChoiceOption } from '@app/shared/ui/types/choice-group.types';
import { ProffeoUiDateRange } from '@app/shared/ui/types/date-picker.types';
import { convertHourToIsoTime } from '@app/shared/utils/time-utils';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { parse } from 'iso8601-duration';
import { map, Observable } from 'rxjs';
import { TimeRegistrationDetailsComponent } from './time-registration-details/time-registration-details.component';
import { timeReportFormSchema } from './time-report-form.schema';
import { MINUTE_STEPS, TimeReportFormModel, timeToSeconds } from './time-report-form.types';
import {
  buildProjectTaskPreselection,
  toProjectTaskModel
} from './time-report-form/project-task-selector/project-task-selector.component';
import { TimeReportFormComponent } from './time-report-form/time-report-form.component';

export interface TimeReportDialogData {
  timeReport?: WorkTimeReport;
  project?: Project;
  task?: Task;
  dateFrom?: Date;
  dateTo?: Date;
  onlyTimerMode?: boolean;
  selectUser?: ReportUserSelector;
}

@Component({
  selector: 'proffeo-time-report-dialog',
  templateUrl: './time-report-dialog.component.html',
  providers: [TimeReportDialogService],
  imports: [
    NgTemplateOutlet,
    FormField,
    FormRoot,
    MatMenuModule,
    MatIconModule,
    TranslatePipe,
    UiChoiceGroupComponent,
    TimeReportFormComponent,
    TimeRegistrationDetailsComponent,
    TaskTimerComponent,
    UiDialogComponent,
    UiButtonComponent,
    UiTextareaComponent,
    ReportSeriesSectionComponent
  ]
})
export class TimeReportDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<TimeReportDialogComponent>);
  private readonly modalData: TimeReportDialogData = inject(MAT_DIALOG_DATA);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly timeReportsService = inject(TimeReportsDataService);
  private readonly timeReportsActionsService = inject(TimeReportsActionsService);
  private readonly timerService = inject(TimerService);
  private readonly timeReportDialogService = inject(TimeReportDialogService);
  private readonly projectsService = inject(ProjectsDataService);

  protected readonly authService = inject(AuthService);
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly TimeReportingType = TimeReportingType;
  protected readonly taskTimerMode = TaskTimerMode;
  protected readonly selectUser = this.modalData.selectUser;
  protected readonly task: WritableSignal<Task> = signal(this.modalData.task);
  protected readonly onlyTimerMode: WritableSignal<boolean> = signal(!!this.modalData.onlyTimerMode);
  protected readonly isAddingNewReport: WritableSignal<boolean> = this.timeReportDialogService.isAddingNewReport;
  protected readonly canSelectReportUser = computed(
    () =>
      this.isAddingNewReport() &&
      !this.onlyTimerMode() &&
      this.timeReportsActionsService.isTimeReportAdmin(this.authService.currentUser()?.email)
  );
  protected readonly selectedTimeReportingType: WritableSignal<TimeReportingType> = signal(
    this.initialTimeReportingType(this.modalData.timeReport)
  );
  /** Start of the current (new) report draft, shown as the summary's creation time. */
  protected readonly draftStartedAt = signal(new Date());

  protected readonly model = signal<TimeReportFormModel>(this.initialModel(this.modalData.timeReport));
  protected readonly form = form(this.model, path => {
    timeReportFormSchema(path, {
      reportingType: () => this.selectedTimeReportingType(),
      canSelectUser: () => this.canSelectReportUser(),
      projectTaskLocked: () => !!this.task() || !this.isAddingNewReport()
    });
    this.extendSchema(path);
  });

  /** Shared multi-day series option (test API only, opt-in, create only). */
  protected readonly series = new ReportSeriesForm({
    canUse: (): boolean => this.isAddingNewReport() && !this.onlyTimerMode(),
    dailyRange: (): ReportSeriesDailyRange | undefined =>
      this.selectedTimeReportingType() === TimeReportingType.RANGE ? this.model().timeRange : undefined,
    initialRange: (): ProffeoUiDateRange => {
      const date = this.model().date ?? new Date();
      const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      return { start: day, end: new Date(day.getTime()) };
    }
  });
  protected timeReport: WorkTimeReport | undefined = this.modalData.timeReport;
  protected currentMinuteStep = 1;
  protected minuteSteps = signal<number[]>(MINUTE_STEPS);
  protected activeTimer: Signal<TaskTimer> = computed(() => {
    const timer = this.timerService.activeTimer();
    return timer ?? this.task()?.timer();
  });
  protected isTimerVisible: Signal<boolean> = this.timerService.isTimerActive;
  protected canShowTimerOption = computed(() => !!this.task() && !this.timeReport);
  protected isActiveTimerFromDifferentTask = computed(
    () => this.timerService.isTimerActive() && this.timerService.activeTimer()?.taskId !== this.task()?.id
  );
  protected reportingTypeOptions = computed((): readonly ProffeoUiChoiceOption<TimeReportingType>[] => {
    const options: ProffeoUiChoiceOption<TimeReportingType>[] = [
      { value: TimeReportingType.MANUAL, label: 'time-reports-form-modal-manual' },
      { value: TimeReportingType.RANGE, label: 'time-reports-form-modal-range' }
    ];
    if (!this.canShowTimerOption() || this.series.active()) {
      return options;
    }

    const timerBlocked = this.isActiveTimerFromDifferentTask();
    return [
      {
        value: TimeReportingType.TIMER,
        label: 'time-reports-form-modal-timer',
        disabled: timerBlocked,
        tooltip: timerBlocked ? 'time-reports-form-modal-timer-disabled-tooltip' : undefined
      },
      ...options
    ];
  });
  protected reportingTypeColumns = computed(() => this.reportingTypeOptions().length);

  public constructor() {
    this.timeReportDialogService.initializeFiles(this.modalData.timeReport);
    this.isAddingNewReport.set(!this.timeReport);
  }

  protected get modalTitle(): string {
    if (this.timeReport) {
      return 'time-reports-form-modal-edit-time-report';
    }
    return 'task-details-modal-add-time-report';
  }

  /** Extension point for subclasses: extra rules on the same form (called once, from the schema). */
  protected extendSchema(path: SchemaPathTree<TimeReportFormModel>): void {
    void path;
  }

  protected enrichReport(report: WorkTimeReport): void {
    void report;
  }

  protected timePickerValueFromIsoDuration(duration?: string): TimePickerValue {
    if (duration) {
      const parsed = parse(duration);
      return { hours: parsed.hours ?? 0, minutes: parsed.minutes ?? 0 };
    }
    return { hours: 0, minutes: 0 };
  }

  protected isoDurationFromTimePicker(value: TimePickerValue | undefined): string | undefined {
    if (!value || timeToSeconds(value) === 0) {
      return undefined;
    }
    return convertHourToIsoTime(value.hours, value.minutes);
  }

  protected onTimeReportingTypeChange(type: TimeReportingType | null): void {
    if (type == null) {
      return;
    }
    this.selectedTimeReportingType.set(type);
  }

  protected onMinuteStepChange(step: number): void {
    this.currentMinuteStep = step;
  }

  protected startTimer(): void {
    if (!this.task()) {
      this.toastService.warning(this.translateService.instant('time-reports.toasts.no-task-selected'));
      return;
    }
    this.timerService.startTimer(this.task());
  }

  protected stopTimer(): void {
    if (this.form.comment().invalid()) {
      this.form().markAsTouched();
      return;
    }
    if (!this.task()?.id) return;

    this.timerService.stopTimer(this.task(), {
      comment: this.model().comment
    });
    this.dialogRef.close(true);
  }

  protected saveTimeReport(shouldClose: boolean): void {
    if (this.form().submitting()) {
      return;
    }

    this.series.markSubmitted();
    if (!this.series.valid()) {
      this.form().markAsTouched();
      this.warnFormInvalid();
      return;
    }

    // `submit` reveals every error (touches all fields) and runs the action only when the form is valid.
    void submit(this.form, {
      action: () => this.persistTimeReport(shouldClose),
      onInvalid: () => this.warnFormInvalid()
    });
  }

  protected saveWorkTimeReport(report: WorkTimeReport, taskId: string): Observable<unknown> {
    if (this.timeReport) {
      return this.timeReportsService.updateWorkTimeReport(report).pipe(map(updatedReport => [updatedReport]));
    }

    const selectedUserId = this.model().userId;
    const currentUserId = this.authService.currentUser()?.id;
    if (this.canSelectReportUser() && selectedUserId && selectedUserId !== currentUserId) {
      return this.timeReportsService.addWorkTimeReportForUser(
        taskId,
        selectedUserId,
        report,
        this.timeReportDialogService.previewFiles()
      );
    }

    return this.timeReportsService.addWorkTimeReport(
      taskId,
      report,
      this.timeReportDialogService.previewFiles(),
      undefined,
      undefined
    );
  }

  /** Sequential per-day creates through the shared series path; attachments go to the first day. */
  protected saveWorkTimeReportSeries(report: WorkTimeReport, taskId: string): Observable<unknown> {
    const selectedUserId = this.model().userId;
    const currentUserId = this.authService.currentUser()?.id;
    const userId =
      this.canSelectReportUser() && selectedUserId && selectedUserId !== currentUserId ? selectedUserId : undefined;

    return this.timeReportsService.addWorkTimeReportSeries(taskId, report, this.series.buildSeries(report.duration), {
      userId,
      files: this.timeReportDialogService.previewFiles()
    });
  }

  protected buildReport(): WorkTimeReport {
    const report = this.timeReport ?? new WorkTimeReport({});
    const value = this.model();

    report.date = this.reportDate(value.date!);
    report.description = value.comment;
    report.hoursType = value.hourType ?? undefined;
    report.duration = this.calculateDuration();
    report.reportType = 'WorkTime';
    report.standardHoursDuration = undefined;
    report.bankHoursDuration = undefined;
    report.overtime50Duration = undefined;
    report.overtime100Duration = undefined;

    this.setReportDates(report, value);
    this.enrichReport(report);

    return report;
  }

  protected calculateDuration(): string | undefined {
    const type = this.selectedTimeReportingType();

    switch (type) {
      case TimeReportingType.MANUAL: {
        const manual = this.model().manualTime;
        return convertHourToIsoTime(manual.hours, manual.minutes);
      }
      case TimeReportingType.RANGE:
        // API accepts either Duration or DateFrom/DateTo — not both.
        return undefined;
      case TimeReportingType.TIMER:
        return this.timeReport?.duration ?? 'PT0S';
      default:
        throw new Error('Unsupported time reporting type');
    }
  }

  private initialTimeReportingType(report: WorkTimeReport | undefined): TimeReportingType {
    if (this.onlyTimerMode()) {
      return TimeReportingType.TIMER;
    }

    if (!report) {
      return TimeReportingType.MANUAL;
    }

    switch (report.registration?.registrationType) {
      case 'Manual':
        return TimeReportingType.MANUAL;
      case 'Timer':
      case 'Range':
        return TimeReportingType.RANGE;
      default:
        return TimeReportingType.RANGE;
    }
  }

  /** The full model of the current report (edited report, Task context or a blank new report). */
  private initialModel(report: WorkTimeReport | undefined): TimeReportFormModel {
    const reportUser = report?.createdBy?.() ?? report?.user?.();
    const currentUser = this.authService.currentUser();
    const now = new Date();

    return {
      userId: currentUser?.id ?? null,
      userName: reportUser?.fullName ?? currentUser?.fullName ?? '',
      date: report?.date ? new Date(report.date) : new Date(),
      manualTime: this.timePickerValueFromIsoDuration(report?.duration),
      timeRange: {
        from: {
          hours: report?.dateFrom?.getHours() ?? now.getHours(),
          minutes: report?.dateFrom?.getMinutes() ?? now.getMinutes()
        },
        to: {
          hours: report?.dateTo?.getHours() ?? now.getHours(),
          minutes: report?.dateTo?.getMinutes() ?? now.getMinutes()
        }
      },
      projectTask: toProjectTaskModel(buildProjectTaskPreselection(this.task(), report, this.projectsService)),
      hourType: report?.hoursType ?? 'OrdinaryHours',
      comment: report?.description ?? '',
      manualDurations: {
        standardHoursDuration: this.timePickerValueFromIsoDuration(report?.standardHoursDuration),
        bankHoursDuration: this.timePickerValueFromIsoDuration(report?.bankHoursDuration),
        overtime50Duration: this.timePickerValueFromIsoDuration(report?.overtime50Duration),
        overtime100Duration: this.timePickerValueFromIsoDuration(report?.overtime100Duration)
      }
    };
  }

  private warnFormInvalid(): void {
    this.toastService.warning(this.translateService.instant('time-reports.toasts.form-invalid'));
  }

  /** Writes the report; resolves once the request settles so `form().submitting()` covers it. */
  private persistTimeReport(shouldClose: boolean): Promise<void> {
    const report = this.buildReport();
    const projectTask = this.model().projectTask;
    const taskId = this.task()?.id ?? projectTask.task;

    if (projectTask.location) {
      report.locationId = projectTask.location;
    }

    // A series resolving to a single day keeps the regular single POST (buildReport uses that day).
    const request$ = this.series.isMultiDay()
      ? this.saveWorkTimeReportSeries(report, taskId!)
      : this.saveWorkTimeReport(report, taskId!);

    return new Promise<void>(resolve => {
      request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          if (shouldClose) {
            this.dialogRef.close(true);
          } else {
            this.timeReportsService.loadTimeReports(this.modalData.dateFrom, this.modalData.dateTo);
            this.resetForm();
          }
        },
        error: (err: unknown) => {
          console.error('Failed to save time report:', err);
          resolve();
        },
        complete: () => resolve()
      });
    });
  }

  private setReportDates(report: WorkTimeReport, value: TimeReportFormModel): void {
    if (this.selectedTimeReportingType() !== TimeReportingType.RANGE) {
      report.dateFrom = undefined;
      report.dateTo = undefined;
      return;
    }

    const baseDate = new Date(this.reportDate(value.date!));
    const dateFrom = new Date(baseDate);
    const dateTo = new Date(baseDate);
    const { from, to } = value.timeRange;

    dateFrom.setHours(from.hours, from.minutes, 0, 0);
    dateTo.setHours(to.hours, to.minutes, 0, 0);

    report.dateFrom = dateFrom;
    report.dateTo = dateTo;
  }

  /** The single day of a one-day series replaces the (hidden) date field; otherwise the date field. */
  private reportDate(formDate: Date): Date {
    return this.series.singleDate() ?? formDate;
  }

  /** Save (stay open): the same form starts over as a blank new report. */
  private resetForm(): void {
    this.series.reset();
    this.timeReport = undefined;
    this.timeReportDialogService.initializeFiles(undefined);
    this.isAddingNewReport.set(true);
    this.selectedTimeReportingType.set(this.initialTimeReportingType(undefined));
    this.draftStartedAt.set(new Date());
    this.model.set(this.initialModel(undefined));
    this.form().reset();
  }
}
